import React from 'react';

export interface BoardSummary {
  readonly boardId: string;
  readonly title: string;
  readonly color: string;
  readonly starred: boolean;
  readonly memberCount: number;
  readonly role?: string;
}

export interface BoardListProps {
  readonly boards: BoardSummary[];
  readonly onBoardClick: (boardId: string) => void;
  readonly onCreateBoard: () => void;
  readonly onStarToggle?: (boardId: string, currentlyStarred: boolean) => void;
}

/** Default board color when none is set */
const DEFAULT_BOARD_COLOR = '#0079bf';

function getBoardColor(color: string): string {
  if (color) return color;
  return DEFAULT_BOARD_COLOR;
}

/**
 * BoardList — displays user's boards as colored tiles in a responsive grid.
 * Inspired by Wekan's board list: colored tiles with white text, star toggle,
 * and an "Add Board" tile.
 *
 * Boards are split into "My Boards" (role === 'owner') and "Shared with me"
 * (any other role).
 */
export function BoardList({ boards, onBoardClick, onCreateBoard, onStarToggle }: BoardListProps): React.ReactElement {
  const myBoards = boards.filter((b) => !b.role || b.role === 'owner');
  const sharedBoards = boards.filter((b) => b.role && b.role !== 'owner');

  const starred = myBoards.filter((b) => b.starred);
  const unstarred = myBoards.filter((b) => !b.starred);

  return (
    <div className="space-y-6 p-6">
      <h2 className="text-2xl font-bold text-foreground">My Boards</h2>

      {starred.length > 0 && (
        <section aria-labelledby="starred-heading">
          <h3 id="starred-heading" className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <span className="text-yellow-400">★</span> Starred Boards
          </h3>
          <BoardGrid boards={starred} onBoardClick={onBoardClick} onStarToggle={onStarToggle} indexOffset={0} />
        </section>
      )}

      <section aria-labelledby="all-heading">
        <div className="flex items-center justify-between mb-4">
          <h3 id="all-heading" className="text-lg font-semibold text-foreground">All Boards</h3>
        </div>

        {unstarred.length === 0 && starred.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No boards yet. Create your first board to get started.</p>
            <button
              type="button"
              onClick={onCreateBoard}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <span aria-hidden="true">+</span> Create Board
            </button>
          </div>
        ) : (
          <BoardGrid
            boards={unstarred}
            onBoardClick={onBoardClick}
            onStarToggle={onStarToggle}
            onCreateBoard={onCreateBoard}
            indexOffset={starred.length}
          />
        )}
      </section>

      {sharedBoards.length > 0 && (
        <section aria-labelledby="shared-heading">
          <h3 id="shared-heading" className="text-lg font-semibold text-foreground mb-4">Shared with me</h3>
          <BoardGrid boards={sharedBoards} onBoardClick={onBoardClick} onStarToggle={onStarToggle} indexOffset={myBoards.length} />
        </section>
      )}
    </div>
  );
}

interface BoardGridProps {
  readonly boards: BoardSummary[];
  readonly onBoardClick: (id: string) => void;
  readonly onStarToggle?: (boardId: string, currentlyStarred: boolean) => void;
  readonly onCreateBoard?: () => void;
  readonly indexOffset: number;
}

function BoardGrid({ boards, onBoardClick, onStarToggle, onCreateBoard, indexOffset }: BoardGridProps): React.ReactElement {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" role="list">
      {boards.map((board, i) => (
        <li key={board.boardId} className="relative">
          <button
            type="button"
            onClick={() => onBoardClick(board.boardId)}
            className="block w-full text-left rounded-lg border border-border p-4 min-h-[100px] font-bold text-white shadow-sm hover:brightness-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            style={{ backgroundColor: getBoardColor(board.color) }}
            aria-label={`Open board: ${board.title}`}
          >
            <span
              role="img"
              aria-label={`Board color: ${getBoardColor(board.color)}`}
              className="absolute top-0 left-0 w-full h-1 rounded-t-lg"
              style={{ backgroundColor: getBoardColor(board.color) }}
            />
            <span className="block text-sm leading-snug break-words">{board.title}</span>
            <span className="block mt-2 text-xs font-normal opacity-75">
              {board.memberCount} member{board.memberCount !== 1 ? 's' : ''}
            </span>
          </button>

          {onStarToggle && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onStarToggle(board.boardId, board.starred); }}
              className="absolute top-2 right-2 p-1 rounded text-white/70 hover:text-yellow-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={board.starred ? `Unstar board: ${board.title}` : `Star board: ${board.title}`}
              aria-pressed={board.starred}
            >
              <span className={board.starred ? 'text-yellow-300' : ''}>
                {board.starred ? '★' : '☆'}
              </span>
            </button>
          )}
        </li>
      ))}

      {onCreateBoard && (
        <li>
          <button
            type="button"
            onClick={onCreateBoard}
            className="flex w-full items-center justify-center rounded-lg border border-dashed border-border p-4 min-h-[100px] bg-card text-card-foreground shadow-sm hover:border-muted-foreground/50 hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label="Create Board"
          >
            <span className="text-sm font-medium">+ Create Board</span>
          </button>
        </li>
      )}
    </ul>
  );
}
